import { styled } from "@linaria/react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { observer } from "mobx-react-lite"
import { useRef, useState } from "react"

import { Horizontal } from "@/components/Flex.ts"
import { ResizableSplitterModel } from "@/components/ResizableSplitter/ResizableSplitterModel.ts"
import { TooltipContent } from "@/components/Tooltip/Tooltip.tsx"
import { useModel } from "@/hooks/useModel.ts"
import { isMac } from "@/lib/userAgent.ts"

import Handle from "./handle.svg?react"

import type { MouseEvent } from "react"

type ResizableSplitterProps = {
  onResize: (leftPercentage: number, rightPercentage: number) => void
  initialLeftPercentage: number
  minLeftPercentage: number
  maxLeftPercentage: number
  minLeftPixels: number
  minRightPixels: number
}

export const ResizableSplitter = observer((props: ResizableSplitterProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [cursorY, setCursorY] = useState<number | null>(null)

  const model = useModel(() => new ResizableSplitterModel({
    onResize: props.onResize,
    initialLeftPercentage: props.initialLeftPercentage,
    minLeftPercentage: props.minLeftPercentage,
    maxLeftPercentage: props.maxLeftPercentage,
    minLeftPixels: props.minLeftPixels,
    minRightPixels: props.minRightPixels,
  }))

  const handleClick = (event: MouseEvent) => {
    if (event.altKey) {
      model.resetToDefault()
    }
  }

  const handleMouseDown = (event: MouseEvent) => {
    const container = containerRef.current?.parentElement
    if (container) {
      model.startDrag(event, container)
    }
  }

  const handleMouseMove = (event: MouseEvent) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      setCursorY(event.clientY - rect.top)
    }
  }

  const handleMouseLeave = () => {
    setCursorY(null)
  }

  // A custom tooltip that follows the cursor
  return (
    <TooltipPrimitive.Root delayDuration={600}>
      <TooltipPrimitive.Trigger asChild>
        <SplitterHandle
          ref={containerRef}
          isDragging={model.isDragging.get()}
          onClick={handleClick}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          data-testid="splitter"
        >
          <Handle />
        </SplitterHandle>
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        {!model.isDragging.get() && cursorY !== null && (
          <TooltipContent
            side="left"
            align="start"
            sideOffset={8}
            alignOffset={cursorY}
            sticky="always"
          >
            {isMac ? "⌥" : "Alt"}+Click to reset
          </TooltipContent>
        )}
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
})

const SplitterHandle = styled(Horizontal)<{ isDragging: boolean }>`
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;

  position: relative;

  width: 8px;
  height: 100%;
  margin-left: -8px;

  cursor: col-resize;
  background: transparent;
  z-index: 1;

  .svg-icon {
    width: 4px;
    height: 100%;

    opacity: ${props => props.isDragging ? 1 : 0};
    transition: opacity 0.2s ease;
  }

  &:hover .svg-icon,
  &:active .svg-icon {
    opacity: 1;
  }
`
